import React from 'react';
import { ChatMessage } from '../../context/ChatProvider';

// ── Minimal markdown → JSX ────────────────────────────────────────────────────

function renderInline(text: string, baseKey: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // Matches **bold** and [label](url)
  const re = /(\*\*(.+?)\*\*|\[(.+?)\]\((https?:\/\/[^\s)]+)\))/g;
  let last = 0;
  let m: RegExpExecArray | null;

  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));

    if (m[0].startsWith('**')) {
      parts.push(<strong key={`${baseKey}-b${m.index}`}>{m[2]}</strong>);
    } else {
      parts.push(
        <a
          key={`${baseKey}-a${m.index}`}
          href={m[4]}
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-dotted hover:decoration-solid"
        >
          {m[3]}
        </a>,
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function parseMarkdown(text: string, msgId: string): React.ReactNode[] {
  const lines = text.split('\n');
  const nodes: React.ReactNode[] = [];
  const bullets: string[] = [];

  function flushBullets(key: string) {
    if (!bullets.length) return;
    nodes.push(
      <ul
        key={key}
        style={{ margin: '2px 0', paddingInlineStart: '16px', listStyleType: 'disc' }}
      >
        {bullets.map((item, i) => (
          <li key={i} style={{ marginTop: 0 }}>
            {renderInline(item, `${key}-li${i}`)}
          </li>
        ))}
      </ul>,
    );
    bullets.length = 0;
  }

  lines.forEach((line, i) => {
    const bulletMatch = line.match(/^[-*]\s+(.*)/);
    if (bulletMatch) {
      bullets.push(bulletMatch[1]);
    } else {
      flushBullets(`${msgId}-ul${i}`);
      if (line.trim() === '') {
        nodes.push(<br key={`${msgId}-br${i}`} />);
      } else {
        nodes.push(
          <span key={`${msgId}-p${i}`} className="block leading-relaxed">
            {renderInline(line, `${msgId}-${i}`)}
          </span>,
        );
      }
    }
  });
  flushBullets(`${msgId}-ul-end`);
  return nodes;
}

// ── Timestamp ─────────────────────────────────────────────────────────────────

function formatTime(date: Date): string {
  const diff = Date.now() - date.getTime();
  if (diff < 60_000) return 'Just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ── Component ─────────────────────────────────────────────────────────────────

/* eslint-disable no-unused-vars */
interface Props {
  message: ChatMessage;
  onRetry: (id: string) => void;
}
/* eslint-enable no-unused-vars */

export function MessageBubble({ message, onRetry }: Props) {
  const isUser = message.role === 'user';
  const isError = message.status === 'error';

  return (
    <div
      className={`group flex items-end gap-2 px-4 py-1 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
    >
      {/* Avatar (Aria only) */}
      {!isUser && (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#1b312e] text-xs font-semibold text-white">
          A
        </div>
      )}

      <div className={`flex max-w-[78%] flex-col gap-0.5 ${isUser ? 'items-end' : 'items-start'}`}>
        {/* Bubble */}
        <div
          className={[
            'aria-chat rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
            isUser
              ? 'rounded-br-sm bg-[#53978e] text-white'
              : 'rounded-bl-sm bg-[#DDECEB] text-[#1b312e]',
            isError ? 'opacity-70' : '',
          ].join(' ')}
        >
          {isUser ? (
            <span className="block leading-relaxed">{message.content}</span>
          ) : (
            parseMarkdown(message.content, message.id)
          )}
        </div>

        {/* Timestamp + retry */}
        <div
          className={`flex items-center gap-2 text-[10px] text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 ${isUser ? 'flex-row-reverse' : ''}`}
        >
          <span>{formatTime(message.createdAt)}</span>
          {isError && (
            <button
              onClick={() => onRetry(message.id)}
              className="font-medium text-red-500 hover:text-red-600"
            >
              Failed · Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
